import { Dispatch, Fragment, ReactElement } from 'react';
import { FormikErrors, FormikValues } from 'formik';
import cn from 'classnames';

import config from '../../config.json';
import cms from '../../data/cms.json';
import styles from '../_pages/Step2/Step2.module.scss';
import { TextField } from '../TextField/TextField';
import { formatPrice } from '../../utils/formatPrice';
import { resolveCoverageBoxTitle } from '../../utils/resolveCoverageBoxTitle';

import { ToggleButton } from '../ToggleButton/ToggleButton';
import { Grid } from '../Grid/Grid';
import { FormItemError } from '../FormItemError/FormItemError';
import { getDisabilityVariantId, getSDVariantId } from '../../utils/getVariantId';
import { DisabilityDetailProps, DisabilityLevelConsequenceProps } from '../../types/initData';
import { ContextProps } from '../../context/AppContext';
import { ProtectionVariantProps } from '../../types/model';
import { triggerGtmEvent } from '../../utils/triggerGtmEvent';
import { Icon } from '../Icon/Icon';

// ### Component renders content form boxes in step2 and step3

export interface CoverageItemProps {
    data: Array<ProtectionVariantProps>;
    code: string;
    groupCode: string;
    ctx: ContextProps;
    activeItem: string;
    setActiveItem: Dispatch<React.SetStateAction<string>>;
    simple: boolean;
    errors: FormikErrors<FormikValues>;
    selectedVariant?: number;
    parentIndex?: number;
    popupContent?: ReactElement;
    buttonLoading?: boolean;
    details?: Array<DisabilityDetailProps>;
    hasCheckedCheckbox?: boolean;
}

export const CoverageItem = ({
    data,
    code,
    groupCode,
    ctx,
    activeItem,
    setActiveItem,
    simple,
    errors,
    selectedVariant,
    parentIndex,
    popupContent,
    buttonLoading,
    details,
    hasCheckedCheckbox,
}: CoverageItemProps): ReactElement => {
    const csq = ctx.formResult.Participants[0].InsuredEventConsequence;
    const childrenSummary = ctx.formResult.Participants[0].ChildrenSummary;
    const orphansPension = csq?.DeathConsequence.OrphansPension || 0;
    const sicknessBenefit = csq?.ShortTermDisabilityConsequence?.SicknessBenefit || 0;
    const disabilityConsequences: Array<DisabilityLevelConsequenceProps> =
        csq?.DisabilityConsequence?.DisabilityLevelConsequences || [];

    const hasChildren = (selectedVariant || 0) > 1 && childrenSummary.HasChildren;

    // ### Separate variants for disability for passed data and make a new array (only in disability group)
    const newData = [];
    if (code === config.RIDER_GROUPS.DIS123 || code === config.RIDER_GROUPS.DISD123) {
        const variant3 = data.filter((itm) => itm.SuggestedRiderVersion.Code === config.RIDER_GROUPS.DISD3);
        const variant2 = data.filter((itm) => itm.SuggestedRiderVersion.Code === config.RIDER_GROUPS.DISD23);
        const variant1 = data.filter((itm) => itm.SuggestedRiderVersion.Code === config.RIDER_GROUPS.DISD123);
        newData.push(variant3, variant2, variant1);
    } else {
        newData.push(data);
    }

    return (
        <>
            {newData.map((item, j: number) => {
                const currentItem = item[j];
                const selectedItem = item[(selectedVariant || 1) - 1];
                const errorsArray =
                    errors[`${selectedItem.SuggestedRiderVersion.Code}-${parentIndex}-${selectedVariant}`];
                const processedError =
                    Array.isArray(errorsArray) && errorsArray.length > 0
                        ? (errorsArray[0] as string)
                        : (errorsArray as string);

                const isOpened = currentItem.SuggestedRiderVersion.Code === activeItem;

                return (
                    <Fragment key={j}>
                        <Grid
                            colsUneven={[1, 2]}
                            nonResponsive
                            className={cn(styles.productList, isOpened && styles.productListOpened)}
                            data-test={`coverageItem[${j}]`}
                        >
                            {!simple &&
                            (groupCode !== config.RIDER_CATEGORIES.DTH ||
                                hasChildren) /* toggle button for death only if person has childrens*/ ? (
                                <div
                                    className={styles.productsLeftTitle}
                                    onClick={() =>
                                        setActiveItem(isOpened ? '' : currentItem.SuggestedRiderVersion.Code)
                                    }
                                    data-test={`coverageItem[${j}]Label`}
                                >
                                    <ToggleButton
                                        className={styles.toggleButton}
                                        opened={isOpened}
                                        onClick={() => setActiveItem(currentItem.SuggestedRiderVersion.Code)}
                                        data-test={`coverageItem[${j}]ToggleButton`}
                                    />
                                    <div className={styles.pointer} data-test={`coverageItem[${j}]Name`}>
                                        <strong>
                                            {resolveCoverageBoxTitle(currentItem.SuggestedRiderVersion.Code)}
                                        </strong>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.productsLeftTitle}>
                                    <div data-test={`coverageItem[${j}]Name`}>
                                        <strong>
                                            {resolveCoverageBoxTitle(currentItem.SuggestedRiderVersion.Code)}
                                        </strong>
                                    </div>
                                </div>
                            )}
                            {/* Render text field and edit button */}
                            <div className={styles.level} data-test={`coverageItem[${j}]ValueWrapper`}>
                                {selectedItem.IsEditable ? (
                                    <div className={styles.levelContent}>
                                        <TextField
                                            name={`${selectedItem.SuggestedRiderVersion.Code}-${parentIndex}-${selectedVariant}`}
                                            variant="price"
                                            label="Pojistná částka"
                                            suffix={config.CURRENCIES.CZK}
                                            className={styles.levelField}
                                            min={
                                                groupCode === config.RIDER_CATEGORIES.DIS
                                                    ? details?.find(
                                                          (detail) =>
                                                              detail.DisabilityLevel ===
                                                              getDisabilityVariantId(item[0].SuggestedRiderVersion.Code)
                                                      )?.SumInsuredMin
                                                    : selectedItem.SumInsuredMin
                                            }
                                            max={
                                                groupCode === config.RIDER_CATEGORIES.DIS
                                                    ? details?.find(
                                                          (detail) =>
                                                              detail.DisabilityLevel ===
                                                              getDisabilityVariantId(item[0].SuggestedRiderVersion.Code)
                                                      )?.SumInsuredMax
                                                    : selectedItem.SumInsuredMax
                                            }
                                            error={hasCheckedCheckbox && processedError ? processedError : undefined}
                                            allowFieldValidation={hasCheckedCheckbox}
                                            data-test={`coverageItem[${j}]Value`}
                                        />

                                        {errors[
                                            `${selectedItem.SuggestedRiderVersion.Code}-${parentIndex}-${selectedVariant}`
                                        ] &&
                                            hasCheckedCheckbox && (
                                                <FormItemError
                                                    className={styles.error}
                                                    text={processedError}
                                                    data-test={`coverageItem[${j}]ValueError`}
                                                />
                                            )}

                                        {groupCode === config.RIDER_CATEGORIES.DTH && (selectedVariant || 0) > 1 && (
                                            <div
                                                onClick={() => {
                                                    ctx.setCloseButton(true);
                                                    ctx.setPopupVisible(true);
                                                    popupContent && ctx.setPopupContent(popupContent);
                                                    ctx.setPopupContentVariables({ uncloseable: false });
                                                    ctx.setPopupCloseEvent('suggest_end');
                                                    triggerGtmEvent('suggest');
                                                }}
                                                className={cn(styles.adjustButton, buttonLoading && styles.isLoading)}
                                                data-test={`coverageItem[${j}]EditButton`}
                                            >
                                                <Icon className={styles.adjustIcon} name="edit" />
                                                {cms.common.editButton}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.fixedPrice} data-test={`coverageItem[${j}]Price`}>
                                        {formatPrice(selectedItem.SumInsuredChosen)} {config.CURRENCIES.CZK}
                                    </div>
                                )}
                            </div>
                        </Grid>
                        {!simple && isOpened && (
                            <Grid
                                cols="auto"
                                className={styles.productDetails}
                                data-test={`coverageItem[${j}]OpenedDetails`}
                            >
                                {groupCode === config.RIDER_CATEGORIES.DTH && hasChildren && (
                                    <div
                                        className={styles.productDetailItem}
                                        data-test={`coverageItem[${j}]Detail[orphansPension]`}
                                    >
                                        <div
                                            className={styles.productDetailItemLabel}
                                            data-test={`coverageItem[${j}]Detail[orphansPension]Label`}
                                        >
                                            Od státu budou děti dostávat sirotčí důchod:
                                        </div>
                                        <div
                                            className="ml--auto"
                                            data-test={`coverageItem[${j}]Detail[orphansPension]Value`}
                                        >
                                            {formatPrice(orphansPension)}
                                            &nbsp;{config.CURRENCIES.CZK}
                                        </div>
                                    </div>
                                )}

                                {groupCode === config.RIDER_CATEGORIES.DIS && (
                                    <>
                                        <div
                                            className={styles.productDetailItem}
                                            data-test={`coverageItem[${j}]Detail[DisabilityPension]`}
                                        >
                                            <div
                                                className={styles.productDetailItemLabel}
                                                data-test={`coverageItem[${j}]Detail[DisabilityPension]Label`}
                                            >
                                                <Icon name="checkmark-circle" />
                                                Invalidní důchod od státu:
                                            </div>

                                            <div
                                                className="ml--auto"
                                                data-test={`coverageItem[${j}]Detail[DisabilityPension]Price`}
                                            >
                                                {formatPrice(
                                                    disabilityConsequences.find(
                                                        (cons) =>
                                                            cons.DisabilityLevel ===
                                                            getDisabilityVariantId(item[0].SuggestedRiderVersion.Code)
                                                    )?.DisabilityPension || 0
                                                )}
                                                &nbsp;{config.CURRENCIES.CZK}
                                            </div>
                                        </div>
                                        {item[0].SuggestedRiderVersion.Code !== config.RIDER_GROUPS.DISD3 && (
                                            <div
                                                className={styles.productDetailItem}
                                                data-test={`coverageItem[${j}]Detail[PossibleIncome]`}
                                            >
                                                <div
                                                    className={styles.productDetailItemLabel}
                                                    data-test={`coverageItem[${j}]Detail[PossibleIncome]Label`}
                                                >
                                                    <Icon name="checkmark-circle" />
                                                    Zvládnete vydělat:
                                                </div>
                                                <div
                                                    className="ml--auto"
                                                    data-test={`coverageItem[${j}]Detail[PossibleIncome]Price`}
                                                >
                                                    {formatPrice(
                                                        disabilityConsequences.find(
                                                            (cons) =>
                                                                cons.DisabilityLevel ===
                                                                getDisabilityVariantId(
                                                                    item[0].SuggestedRiderVersion.Code
                                                                )
                                                        )?.PossibleIncome || 0
                                                    )}
                                                    &nbsp;{config.CURRENCIES.CZK}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {groupCode === config.RIDER_CATEGORIES.SD && (
                                    <div
                                        className={styles.productDetailItem}
                                        data-test={`coverageItem[${j}]Detail[benefitPaymentDate]`}
                                    >
                                        <div
                                            className={styles.productDetailItemLabel}
                                            data-test={`coverageItem[${j}]Detail[benefitPaymentDate]Label`}
                                        >
                                            <Icon name="checkmark-circle" />
                                            Dávka je vyplácena:
                                        </div>
                                        {groupCode === config.RIDER_CATEGORIES.SD &&
                                            getSDVariantId(config.RIDER_GROUPS.SD28) === selectedVariant && (
                                                <div
                                                    className="ml--auto"
                                                    data-test={`coverageItem[${j}]Detail[benefitPaymentDate]Value`}
                                                >
                                                    od 28. dne
                                                </div>
                                            )}
                                        {groupCode === config.RIDER_CATEGORIES.SD &&
                                            getSDVariantId(config.RIDER_GROUPS.SD56) === selectedVariant && (
                                                <div
                                                    className="ml--auto"
                                                    data-test={`coverageItem[${j}]Detail[benefitPaymentDate]Value`}
                                                >
                                                    od 56. dne
                                                </div>
                                            )}
                                        {groupCode === config.RIDER_CATEGORIES.SD &&
                                            getSDVariantId(config.RIDER_GROUPS.SD90) === selectedVariant && (
                                                <div
                                                    className="ml--auto"
                                                    data-test={`coverageItem[${j}]Detail[benefitPaymentDate]Value`}
                                                >
                                                    od 90. dne
                                                </div>
                                            )}

                                        {sicknessBenefit > 0 && (
                                            <>
                                                <div
                                                    className={styles.productDetailItemLabel}
                                                    data-test={`coverageItem[${j}]Detail[sicknessBenefit]Label`}
                                                >
                                                    <Icon name="checkmark-circle" />
                                                    Nemocenská od státu:
                                                </div>
                                                <div
                                                    className="ml--auto"
                                                    data-test={`coverageItem[${j}]Detail[SD]Detail[sicknessBenefit]Value`}
                                                >
                                                    {formatPrice(sicknessBenefit || 0)} {config.CURRENCIES.CZK}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {(groupCode === config.RIDER_CATEGORIES.SD ||
                                    groupCode === config.RIDER_CATEGORIES.DIS) &&
                                    item.filter((target) => target?.LossOfIncome > 0).length > 0 && (
                                        <div
                                            className={styles.productDetailItem}
                                            data-test={`coverageItem[${j}]Detail[lossOfIncome]`}
                                        >
                                            <div
                                                className={styles.productDetailItemLabel}
                                                data-test={`coverageItem[${j}]Detail[lossOfIncome]Label`}
                                            >
                                                <Icon name="checkmark-circle" />
                                                Výpadek příjmu:
                                            </div>
                                            <div
                                                className="ml--auto"
                                                data-test={`coverageItem[${j}]Detail[lossOfIncome]Value`}
                                            >
                                                {formatPrice(selectedItem.LossOfIncome || 0)} {config.CURRENCIES.CZK}
                                            </div>
                                        </div>
                                    )}

                                {groupCode === config.RIDER_CATEGORIES.DIS && (
                                    <div
                                        className={styles.productDetailItem}
                                        data-test={`coverageItem[${j}]Detail[incomeCoveredForMonths]`}
                                    >
                                        <div
                                            className={styles.productDetailItemLabel}
                                            data-test={`coverageItem[${j}]Detail[incomeCoveredForMonths]Label`}
                                        >
                                            <Icon name="checkmark-circle" />
                                            Pojištění dorovná příjmy na:
                                        </div>
                                        <div
                                            className="ml--auto"
                                            data-test={`coverageItem[${j}]Detail[incomeCoveredForMonths]Value`}
                                        >
                                            {selectedItem.IncomeCoveredForMonths || 0} měsíců
                                        </div>
                                    </div>
                                )}
                            </Grid>
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};
